import { useCallback, useEffect, useRef, useState } from "react";
import { addDaysYmd, todayInZone } from "../lib/zone.js";
import { BookingApiError, type BookingApi } from "./api.js";
import { errorMessage, mergeSlots, nextDayWithSlots, type PublicConfig, type Slot } from "./helpers.js";

const WINDOW_DAYS = 14;
/** "Next available day" gives up after this many two-week windows (about three months). */
const MAX_SEARCH_WINDOWS = 6;

/**
 * The free times for the chosen services, loaded two weeks at a time as the customer moves between days. `ensureDay` makes sure a day is
 * loaded; `findNext` looks forward (loading further windows if it has to) for the next day with a time.
 */
export function useSlotDays({ api, config, serviceIds, refreshKey = 0 }: { api: BookingApi; config: PublicConfig; serviceIds: string[]; refreshKey?: number }) {
  const zone = config.timezone;
  const idsKey = serviceIds.join(",");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loaded, setLoaded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const loadedRef = useRef<Set<string>>(new Set());
  const slotsRef = useRef<Slot[]>([]);
  const inflight = useRef(new Map<string, Promise<void>>());

  const lastDay = addDaysYmd(todayInZone(zone), config.max_days_ahead);

  /** Loads the two weeks starting at `from` (once; a repeat while loading shares the request). */
  const loadWindow = useCallback(
    (from: string): Promise<void> => {
      if (serviceIds.length === 0) return Promise.resolve();
      const existing = inflight.current.get(from);
      if (existing) return existing;
      const mine = generation.current;
      setLoading(true);
      setError(null);
      const p = api
        .slots(serviceIds, from, WINDOW_DAYS)
        .then((got) => {
          if (mine !== generation.current) return; // the choice changed while this was loading
          slotsRef.current = mergeSlots(slotsRef.current, got);
          const days = new Set(loadedRef.current);
          for (let i = 0; i < WINDOW_DAYS; i++) days.add(addDaysYmd(from, i));
          loadedRef.current = days;
          setSlots(slotsRef.current);
          setLoaded(days);
        })
        .catch((e) => {
          if (mine === generation.current) setError(errorMessage(e instanceof BookingApiError ? e.code : "unexpected_error"));
        })
        .finally(() => {
          inflight.current.delete(from);
          if (mine === generation.current && inflight.current.size === 0) setLoading(false);
        });
      inflight.current.set(from, p);
      return p;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, idsKey]
  );

  // A different choice of services (or a refresh after a time was taken) starts over from today.
  useEffect(() => {
    generation.current++;
    inflight.current.clear();
    slotsRef.current = [];
    loadedRef.current = new Set();
    setSlots([]);
    setLoaded(new Set());
    setError(null);
    setLoading(false);
    if (serviceIds.length > 0) void loadWindow(todayInZone(zone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, refreshKey, zone]);

  const ensureDay = useCallback(
    (ymd: string) => {
      if (serviceIds.length === 0 || ymd > lastDay || loadedRef.current.has(ymd)) return;
      void loadWindow(ymd);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadWindow, lastDay, idsKey]
  );

  /** The next day after `afterYmd` that has a time, loading further weeks if needed; null if there is none within reach. */
  const findNext = useCallback(
    async (afterYmd: string): Promise<string | null> => {
      for (let i = 0; i <= MAX_SEARCH_WINDOWS; i++) {
        const found = nextDayWithSlots(slotsRef.current, zone, afterYmd);
        if (found) return found;
        // Not loaded far enough yet: load the first day after `afterYmd` that has not been asked for.
        let from = addDaysYmd(afterYmd, 1);
        while (from <= lastDay && loadedRef.current.has(from)) from = addDaysYmd(from, 1);
        if (from > lastDay) return null;
        await loadWindow(from);
      }
      return null;
    },
    [loadWindow, zone, lastDay]
  );

  return { slots, loaded, loading, error, ensureDay, findNext, lastDay };
}
